import { Button } from "alurkerja-ui";
import { LucideDownload, LucideFilter, Printer, RefreshCw } from "lucide-react";

interface DefaultButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  size?: "xs" | "sm" | "md";
  shape?: "sm" | "md" | "lg" | "full";
  mood:
    | "axiata-blue"
    | "axiata-red"
    | "axiata-blue-link"
    | "axiata-blue-outline"
    | "axiata-purple-outline"
    | "axiata-reddish-outline";
}

export function DefaultButton({
  children,
  loading,
  size = "sm",
  shape = "md",
  mood,
  ...props
}: React.PropsWithChildren<DefaultButtonProps>) {
  const moodClass = {
    "axiata-blue": "bg-axiata-blue text-white hover:brightness-125",
    "axiata-red": "bg-axiata-red text-white hover:brightness-110",
    "axiata-blue-link": "text-axiata-blue underline hover:no-underline",
    "axiata-blue-outline":
      "border border-axiata-blue text-axiata-blue hover:bg-axiata-blue hover:text-white dark:text-neutral-300",
    "axiata-purple-outline":
      "border border-axiata-purple text-axiata-purple hover:bg-axiata-purple hover:text-white dark:brightness-200",
    "axiata-reddish-outline":
      "border border-[#DC3545] text-[#DC3545] hover:bg-[#DC3545] hover:text-white",
  };
  return (
    <Button
      {...props}
      loading={loading}
      size={size}
      shape={shape}
      className={moodClass[mood] + " w-fit " + props.className}
    >
      {children}
    </Button>
  );
}

export function FilterButton({
  children,
  ...props
}: Omit<DefaultButtonProps, "mood">) {
  return (
    <DefaultButton {...props} mood="axiata-blue-outline" type="button">
      <span className="flex items-center gap-x-2">
        <LucideFilter size={16} />
        {children ? children : <span className="sr-only">Filter</span>}
      </span>
    </DefaultButton>
  );
}

export function DownloadButton({
  children,
  ...props
}: Omit<DefaultButtonProps, "mood">) {
  return (
    <DefaultButton {...props} mood="axiata-purple-outline" type="button">
      <span className="flex items-center gap-x-2">
        <LucideDownload size={16} />
        {children ? children : <span className="sr-only">Download</span>}
      </span>
    </DefaultButton>
  );
}

export function SyncButton({
  children,
  ...props
}: Omit<DefaultButtonProps, "mood">) {
  return (
    <DefaultButton {...props} mood="axiata-blue-outline" type="button">
      <span className="flex items-center gap-x-2">
        <RefreshCw size={16} />
        {children ? children : <span className="sr-only">Synchronize</span>}
      </span>
    </DefaultButton>
  );
}

export function PrintButton({
  children,
  ...props
}: Omit<DefaultButtonProps, "mood">) {
  return (
    <DefaultButton {...props} mood="axiata-blue-outline" type="button">
      <span className="flex items-center gap-x-2">
        <Printer size={16} />
        {children ? children : <span className="sr-only">Download</span>}
      </span>
    </DefaultButton>
  );
}

export function SubmitButton({
  children,
  ...props
}: Omit<DefaultButtonProps, "mood">) {
  return (
    <DefaultButton {...props} mood="axiata-blue" type={props.type ?? "submit"}>
      {children ? children : "Submit"}
    </DefaultButton>
  );
}

export function RejectButton({
  children,
  ...props
}: Omit<DefaultButtonProps, "mood">) {
  return (
    <DefaultButton
      {...props}
      mood="axiata-reddish-outline"
      type={props.type ?? "submit"}
    >
      {children ? children : "Reject"}
    </DefaultButton>
  );
}
